'use client';

import { c as _c } from "react/compiler-runtime";
import OptimizedImage from '@/components/OptimizedImage';
import { zodResolver } from '@hookform/resolvers/zod';
import { motion, PanInfo } from 'framer-motion';
import { useTranslations } from 'next-intl';
import { ChangeEvent, RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDebounceCallback, useEventListener, useInterval, useResizeObserver } from 'usehooks-ts';
import { z } from 'zod';
import ViverseButton from '@/components/ViverseButton';
import IconCamera from '@/components/assets/icon_camera_2.svg';
import IconCheck from '@/components/assets/icon_check.svg';
import IconMove from '@/components/assets/icon_move.svg';
import IconUpload from '@/components/assets/icon_upload.svg';
import { Form, FormControl, FormField, FormItem } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { ACCEPTED_IMAGE_TYPES, MAX_IMAGE_SIZE } from '@/constants/file';
import { useConfigsContext } from '@/context/ConfigsContext';
import { useRandomBanner } from '@/hooks/useRandomBanner';
import { useUploadErrorMessages } from '@/hooks/useUploadErrorMessages';
import { extractIdFromImageUrl } from '@/lib/asset';
import { validateImageDimensions } from '@/lib/image';
import { showToast } from '@/lib/toast';
import { cn } from '@/lib/utils';
import { updateProfileMeByCmsAccountId } from '@/server/lib/profile';
import { uploadImageAction } from '@/server/lib/upload';
import { BannerMeta } from '@/types/store';
const useFormSchema = () => {
  const $ = _c(2);
  const errorMessages = useUploadErrorMessages();
  let t0;
  if ($[0] !== errorMessages) {
    t0 = z.object({
      banner: z.instanceof(File).refine(_temp, {
        message: errorMessages["profile-banner-file-too-large"].description
      }).nullable()
    });
    $[0] = errorMessages;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const formSchema = t0;
  return formSchema;
};
type ImageDimensions = {
  width: number;
  height: number;
  aspectRatio: number;
};
type ContainerSize = {
  width?: number;
  height?: number;
};
function PrivateProfileBanner() {
  const t = useTranslations();
  const {
    store,
    setStore
  } = useConfigsContext();
  const randomBanner = useRandomBanner();
  const htcToken = store?.credentials?.htcToken;
  const cmsAccountId = store?.profile?.id;
  const defaultBanner = store?.profile?.banner || randomBanner.src;
  const defaultBannerMeta = useMemo(() => store?.profile?.bannerMeta as BannerMeta || {
    y: 0,
    ratio: 0,
    yPercent: 0
  }, [store?.profile?.bannerMeta]);
  const [banner, setBanner] = useState(defaultBanner);
  const [isSaving, setIsSaving] = useState(false);
  const [progress, setProgress] = useState(0);
  const [imagePosition, setImagePosition] = useState<BannerMeta>(defaultBannerMeta);
  const [imageDimensions, setImageDimensions] = useState<ImageDimensions | null>(null);
  const [containerSize, setContainerSize] = useState<ContainerSize>({});
  const onResize = useDebounceCallback(setContainerSize, 200);
  const containerWidth = containerSize.width;
  const containerHeight = containerSize.height;
  const containerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const errorMessages = useUploadErrorMessages();
  const formSchema = useFormSchema();
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      banner: null
    }
  });
  const uploadedBanner = form.watch('banner');
  const handleImageLoad = useCallback((img: HTMLImageElement) => {
    const dimensions = {
      width: img.naturalWidth,
      height: img.naturalHeight,
      aspectRatio: img.naturalWidth / img.naturalHeight
    };
    setImageDimensions(dimensions);
    if (imagePosition.ratio !== dimensions.aspectRatio) {
      setImagePosition(prev => ({
        ...prev,
        ratio: dimensions.aspectRatio
      }));
    }
  }, [imagePosition.ratio]);
  useEffect(() => {
    if (banner && imageRef.current) {
      const img_0 = new window.Image();
      img_0.onload = () => handleImageLoad(img_0);
      img_0.src = banner;
    }
  }, [banner, handleImageLoad]);
  useInterval(() => {
    setProgress(prevProgress => {
      const newProgress = prevProgress + Math.random() * 5;
      return Math.min(newProgress, 99);
    });
  }, isSaving ? 200 : null);
  useEffect(() => {
    if (!isSaving) {
      setProgress(0);
    }
  }, [isSaving]);
  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    if (!htcToken || !cmsAccountId) {
      throw new Error('No HTC token or CMS account ID');
    }
    setIsSaving(true);
    try {
      const uploadResult = await uploadImageAction(data.banner!);
      if (!uploadResult.ok) throw new Error(uploadResult.error);
      const bannerUrl = uploadResult.url;
      const res = await updateProfileMeByCmsAccountId(cmsAccountId, {
        profile_banner: extractIdFromImageUrl(bannerUrl),
        profile_banner_meta: imagePosition
      });
      if (res?.status === 200 && res?.results) {
        setBanner(bannerUrl);
        setStore(value => ({
          ...value,
          profile: {
            ...value.profile!,
            banner: bannerUrl,
            bannerMeta: imagePosition
          }
        }));
        showToast({
          message: t('profile-page.banner.saved'),
          type: 'success'
        });
      } else {
        throw new Error('Failed to update profile banner');
      }
    } catch (error) {
      console.error('Failed to update profile banner: ', error);
    } finally {
      setIsSaving(false);
      form.reset();
    }
  };
  const onCancel = () => {
    setIsSaving(false);
    setBanner(defaultBanner);
    setImagePosition(defaultBannerMeta);
    form.reset();
  };
  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>, onChange: (file: File) => void) => {
    if (!event.target.files) return;
    const file = event.target.files[0];
    if (!ACCEPTED_IMAGE_TYPES.includes(file.type as (typeof ACCEPTED_IMAGE_TYPES)[number])) {
      showToast({
        message: errorMessages['unsupported-file-type-1'].description,
        title: errorMessages['unsupported-file-type-1'].title,
        type: 'error'
      });
    } else if (file.size > MAX_IMAGE_SIZE) {
      showToast({
        message: errorMessages['profile-banner-file-too-large'].description,
        title: errorMessages['profile-banner-file-too-large'].title,
        type: 'error'
      });
    } else {
      const dimensionValidation = await validateImageDimensions(file);
      if (dimensionValidation === 'unreadable') {
        showToast({
          message: errorMessages['unsupported-file-type-1'].description,
          title: errorMessages['unsupported-file-type-1'].title,
          type: 'error'
        });
        return;
      }
      if (dimensionValidation === 'too-large') {
        showToast({
          message: errorMessages['image-dimensions-too-large'].description,
          title: errorMessages['image-dimensions-too-large'].title,
          type: 'error'
        });
        return;
      }
      form.clearErrors('banner');
      onChange(file);
      setBanner(URL.createObjectURL(file));
      setImagePosition({
        y: 0,
        ratio: 0,
        yPercent: 0
      });
    }
  };
  const calculateDragConstraints = useCallback(() => {
    if (!imageDimensions || !containerWidth || !containerHeight || !uploadedBanner) {
      return {
        top: 0,
        bottom: 0
      };
    }
    const currentDeviceRatio = containerWidth / containerHeight;
    const imageRatio = imageDimensions.aspectRatio;
    const scaledHeight = containerWidth / imageRatio;
    if (scaledHeight <= containerHeight) {
      return {
        top: 0,
        bottom: 0
      };
    }
    const maxDragDistance = scaledHeight - containerHeight;
    let targetY = 0;
    if (Number(imagePosition.ratio) > 0) {
      if (currentDeviceRatio <= 4.63) {
        targetY = -(maxDragDistance / 2);
      } else {
        if (imageRatio > currentDeviceRatio) {
          targetY = -(maxDragDistance / 2);
        }
      }
    }
    const finalTargetY = Math.max(-maxDragDistance, Math.min(0, targetY));
    return {
      top: -maxDragDistance,
      bottom: 0,
      targetY: finalTargetY
    };
  }, [containerHeight, containerWidth, imageDimensions, imagePosition.ratio, uploadedBanner]);
  const handleDragEnd = useCallback((_: unknown, info: PanInfo) => {
    const constraints = calculateDragConstraints();
    const minY = constraints.top;
    const maxY = constraints.bottom;
    const newY = Math.max(minY, Math.min(maxY, (imagePosition.y || 0) + info.offset.y));
    const yPercent = (newY - minY) / (maxY - minY);
    setImagePosition(prev_0 => ({
      ...prev_0,
      y: newY,
      yPercent
    }));
  }, [imagePosition.y, calculateDragConstraints]);
  const handleKeyDown = (event_0: KeyboardEvent) => {
    if (!uploadedBanner) return;
    if (event_0.key === 'ArrowUp' || event_0.key === 'ArrowDown') {
      event_0.preventDefault();
      const constraints_0 = calculateDragConstraints();
      const newY_0 = Math.max(constraints_0.top, Math.min(constraints_0.bottom, (imagePosition.y || 0) + (event_0.key === 'ArrowUp' ? 50 : -50)));
      setImagePosition(prev_1 => ({
        ...prev_1,
        y: newY_0
      }));
    }
  };
  useEventListener('keydown', handleKeyDown);
  const dragConstraints = calculateDragConstraints();
  useResizeObserver({
    ref: containerRef as RefObject<HTMLElement>,
    onResize
  });
  const getInitialY = () => {
    if (!imageDimensions || containerWidth === undefined || containerHeight === undefined) return 0;
    const imageRatio_0 = imageDimensions.aspectRatio;
    const scaledHeight_0 = containerWidth / imageRatio_0;
    if (scaledHeight_0 <= containerHeight) return 0;
    const maxDragDistance_0 = scaledHeight_0 - containerHeight;
    const minY_0 = -maxDragDistance_0;
    const maxY_0 = 0;
    return minY_0 + (maxY_0 - minY_0) * (imagePosition.yPercent ?? 0);
  };
  const isReady = typeof containerWidth === 'number' && typeof containerHeight === 'number';
  return <section className="group px-4 py-2 md:px-8 md:py-4 md:pt-8 lg:px-18">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="relative aspect-[358/96] size-full min-h-[96px] overflow-hidden rounded-2xl md:aspect-[704/152] lg:aspect-[1296/280]">
            <div className={cn('z-interactive absolute inset-0 hidden bg-gray-800/30', {
            'block animate-pulse cursor-wait': isSaving
          })} />
            <div ref={containerRef} className="bg-vive-ghost-white/10 relative size-full overflow-hidden rounded-lg">
              <motion.div drag={uploadedBanner ? 'y' : false} dragMomentum={false} dragElastic={0.1} dragConstraints={dragConstraints} onDragEnd={handleDragEnd} initial={{
              y: getInitialY()
            }} animate={{
              y: banner !== defaultBanner ? imagePosition.y : getInitialY()
            }} transition={{
              type: 'spring',
              stiffness: 300,
              damping: 30,
              duration: banner !== defaultBanner ? 0.5 : 0
            }} className={cn('absolute inset-0 select-none', uploadedBanner && dragConstraints.top !== dragConstraints.bottom ? 'cursor-grab active:cursor-grabbing' : 'cursor-default', !isReady && 'opacity-0')}>
                <OptimizedImage ref={imageRef} src={banner} alt="profile banner" className="h-auto w-full object-cover" fill preload sizes="(max-width: 768px) 40vw, (max-width: 1024px) 704px, 1296px" draggable={false} />
              </motion.div>
            </div>

            <div className="body-2 lg:body absolute top-2 right-2 flex items-center md:top-4 md:right-4">
              {uploadedBanner ? <div className="flex gap-4">
                  <ViverseButton className="h-[34px] max-md:px-4 max-md:py-2 max-md:text-sm md:h-[48px]" type="submit">
                    <IconCheck className="mr-2 size-6" />
                    {t('profile-page.banner.save')}
                  </ViverseButton>
                  <ViverseButton variant="secondary" className="h-[34px] max-md:px-4 max-md:py-2 max-md:text-sm md:h-[48px]" onClick={onCancel}>
                    {t('profile-page.banner.cancel')}
                  </ViverseButton>
                </div> : <div className="bg-vive-ghost-white hover:bg-vive-ghost-white-hover animate-in fade-in-0 flex cursor-pointer items-center gap-2 rounded-full px-4 py-2 font-medium backdrop-blur-lg hover:text-white" onClick={() => fileInputRef.current?.click()}>
                  <IconCamera className="size-5 md:size-6" />
                  {t('profile-page.banner.edit')}
                </div>}
            </div>

            <div className="body absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center text-white max-md:w-full">
              {isSaving ? <div className="flex animate-pulse flex-col items-center gap-2 duration-300">
                  <div className="flex items-center gap-2">
                    <IconUpload className="size-5" />
                    <p className="body-2 flex items-center gap-1">
                      {t('avatar-editor.submit-button.uploading')}
                      {Math.round(progress)}%
                    </p>
                  </div>
                  <div className="w-[306px]">
                    <motion.div className="bg-vive-primary-40 animate-pulse-glow h-2 rounded-lg" transition={{
                  duration: 2,
                  ease: 'linear'
                }} initial={{
                  width: 0
                }} animate={{
                  width: `${Math.round(progress)}%`
                }} style={{
                  background: 'linear-gradient(270deg, #CCF0F9 0%, #00A0CB 51.77%, #0589C9 100%)'
                }} />
                  </div>
                </div> : uploadedBanner && <div className="animate-in fade-in-0 subheader-2 pointer-events-none hidden items-center gap-2 text-white max-md:mt-10 max-md:justify-center md:mt-6 md:text-lg lg:mt-0 lg:flex">
                    <IconMove className="size-5" />
                    {t('profile-page.banner.drag-to-reposition')}
                  </div>}
              {!uploadedBanner && <p className="animate-in fade-in-0 subheader-2 hidden group-hover:block max-md:mt-10 md:text-lg">
                  {t('profile-page.banner.optimal-dimensions')}
                </p>}
            </div>
            <FormField control={form.control} name="banner" render={({
            field: {
              onChange: onChange_0
            }
          }) => <FormItem>
                  <FormControl>
                    <Input key={uploadedBanner?.name} ref={fileInputRef} type="file" className="hidden" accept={ACCEPTED_IMAGE_TYPES.join(',')} onChange={event_1 => handleFileChange(event_1, onChange_0)} />
                  </FormControl>
                </FormItem>} />
          </div>
        </form>
      </Form>
    </section>;
}
export default PrivateProfileBanner;
function _temp(file) {
  return file.size < MAX_IMAGE_SIZE;
}